import {
    ActionSystemActionType,
    EStatus,
    IRootState,
    IUpdateWidgetPropertyByPath,
    StoreActionTypeEnum,
    TBottomSheetsWidgetsMap,
    TCommonData,
    TCommonDataObj,
    TCommonWidgetValue,
    Theme,
    TInitWidgetState,
    TModalWidgetsMap,
    TModalWindow,
    TMutableWidgetsData,
    TMutableWidgetsOption,
    TPayloadWidget,
    TStateScenario,
    TSystemActin,
    TSystemActions,
    TWidget,
    TWInternalId,
    ValidationErrors,
} from '@libs/types';
import { createSelector, createSlice, isAnyOf, PayloadAction, UnknownAction, WritableDraft } from '@reduxjs/toolkit';
import { shallowEqual, useSelector } from 'react-redux';
import { getWidgets, updateWidgetPropertyByPathUtils } from '../../foundation';
import { isPendingAction, isPendingWidgetAction, isRejectedAction, isUpdateWidget } from './utils/actions';
import { removeWidget } from './utils/clearMutableWidgets';
import { mergeIntoMap } from './utils/mergeIntoMap';
import { fetchJsonAsync, fetchScenario, updateScenario } from './utils/sharedActions';

interface IUpdateWidgetValue extends TCommonData {
    uid: string;
}

interface IUpdateWidgetValueByInternalId extends TCommonData {
    internalId: TWInternalId;
}

interface IAddMutableWidgets {
    widgets: Array<TPayloadWidget<TCommonData>> | TPayloadWidget<TCommonData>;
    widgetUid: TWidget<TCommonData>['uid'];
}

interface IUpdateWidgetValueByName {
    uid: string;
    key: string;
    value: TCommonWidgetValue;
}

interface ISetBottomSheetIsOpen {
    bsInternalId: string;
    isOpen: boolean;
}

/**
 * `requestId` of a thunk action, read defensively: the shared matchers in `./utils/actions` are typed
 * against `AsyncThunk<unknown, unknown, never>`, which erases `meta` from the narrowed action.
 */
const getRequestId = (action: UnknownAction): string | undefined => (action as { meta?: { requestId?: string } })?.meta?.requestId;

/**
 * Takes the player out of its pending state when the request that put it there ended without
 * producing anything to render.
 *
 * That happens whenever a request is aborted: `fetchJsonAsync` rethrows `AbortError` (rejected with
 * no payload) and `fetchScenario` swallows it (fulfilled with no payload). Neither is a failure, so
 * no error screen — but the status cannot stay pending either: `isShowLoader` is derived from
 * `EStatus.Loading`, so the spinner overlay would hang forever when no later request arrives to
 * settle it.
 *
 * Only the request that set the pending state may clear it — a newer one already owns the status —
 * and only while the status is still pending, so a challenge or a failure raised in the meantime is
 * never overwritten.
 */
const releasePendingState = (state: WritableDraft<TStateScenario>, requestId?: string): void => {
    const isPending = state.status === EStatus.Loading || state.status === EStatus.WidgetLoading;
    if (isPending && requestId && requestId === state.pendingRequestId) {
        state.status = EStatus.Succeeded;
        state.pendingRequestId = undefined;
    }
};

const initialState: TStateScenario = {
    status: EStatus.Idle,
    errors: {
        errorMessage: '',
    },
    scenarioId: '',
    currentPageId: '',
    playerId: undefined,
    theme: {},
    mutableWidgetsMap: {},
    bottomSheetMap: {},
    modalWindowMap: {},
    internalIdWidgets: {},
    submitWidget: undefined,
    systemActions: {} as TSystemActions,
    configuration: {},
};

export const scenarioSlice = createSlice({
    name: 'scenario',
    initialState,
    reducers: {
        setPlayerId(state, { payload }: PayloadAction<string>) {
            state.playerId = payload;
        },
        setStatus: (state, { payload }: PayloadAction<EStatus>) => {
            state.status = payload;
        },
        setTheme: (state, { payload }: PayloadAction<Theme>) => {
            state.theme = payload;
        },
        addMutableWidgets: (state, { payload }: PayloadAction<IAddMutableWidgets>) => {
            const widgets = payload.widgets;
            if (widgets) {
                const mutableWidgets = getWidgets(Array.isArray(widgets) ? widgets : [widgets]);
                removeWidget(state.mutableWidgetsMap, payload.widgetUid);
                mergeIntoMap(
                    state.mutableWidgetsMap,
                    mutableWidgets.mutableWidgetsMap as Record<string, WritableDraft<TWidget<TMutableWidgetsData, TMutableWidgetsOption>>>,
                );
                if (mutableWidgets.submitWidget) {
                    state.submitWidget = mutableWidgets.submitWidget;
                }
                if (mutableWidgets.systemActions) {
                    for (const [type, action] of Object.entries(mutableWidgets.systemActions) as Array<[ActionSystemActionType, TSystemActin]>) {
                        if (action && !state.systemActions[type]) {
                            state.systemActions[type] = action as WritableDraft<TSystemActin>;
                        }
                    }
                }
            }
        },
        updateWidgetValue: (state, { payload }: PayloadAction<IUpdateWidgetValue>) => {
            const widget = state?.mutableWidgetsMap?.[payload.uid];
            if (widget && widget?.data?.value !== payload.value) {
                widget['data'] = { ...widget['data'], value: payload.value };
            }
        },
        updateWidgetValueByInternalId: (state, { payload }: PayloadAction<IUpdateWidgetValueByInternalId>) => {
            if (payload.internalId) {
                const widgetUid = state?.internalIdWidgets?.[payload.internalId];
                const widget = state?.mutableWidgetsMap?.[widgetUid];
                if (widget && widget?.data?.value !== payload.value) {
                    widget['data'] = { ...widget['data'], value: payload.value };
                }
            }
        },
        updateWidgetValueByKey: (state, { payload }: PayloadAction<IUpdateWidgetValueByName>) => {
            const widget = state?.mutableWidgetsMap?.[payload.uid];
            if (widget) {
                const currentValue = widget?.data?.value as TCommonDataObj;
                const newValue = { ...currentValue, [payload.key]: payload.value } as TCommonDataObj;
                widget['data'] = { ...widget['data'], value: newValue };
            }
        },
        updateWidgetDataByKey: (state, { payload }: PayloadAction<IUpdateWidgetValueByName>) => {
            const widget = state?.mutableWidgetsMap?.[payload.uid];
            if (widget) {
                widget['data'] = { ...widget['data'], [payload.key]: payload.value };
            }
        },
        updateWidgetPropertyByPath: (state, { payload }: PayloadAction<IUpdateWidgetPropertyByPath>) => {
            const widget = state?.mutableWidgetsMap?.[payload.uid ?? payload.widget?.uid ?? ''];
            updateWidgetPropertyByPathUtils({ widget, value: payload.value, path: payload.path });
        },
        updateBottomSheetsWidgetsMap: (state, { payload }: PayloadAction<TBottomSheetsWidgetsMap>) => {
            mergeIntoMap(state.bottomSheetMap, payload);
        },
        setBottomSheetIsOpen: (state, { payload }: PayloadAction<ISetBottomSheetIsOpen>) => {
            const sheet = state.bottomSheetMap?.[payload.bsInternalId] as TModalWindow;
            if (sheet) {
                sheet['isOpen'] = payload.isOpen;
            }
        },
        updateModalWindowsMap: (state, { payload }: PayloadAction<TModalWidgetsMap>) => {
            mergeIntoMap(state.modalWindowMap, payload);
        },
        updateInternalIdWidgets: (state, { payload }: PayloadAction<TInitWidgetState['internalIdWidgets']>) => {
            mergeIntoMap(state.internalIdWidgets, payload);
        },
    },
    extraReducers: (builder) => {
        builder
            .addMatcher(isAnyOf(fetchScenario.fulfilled, updateScenario.fulfilled), (state, { payload, meta }) => {
                if (!payload) {
                    // Aborted `fetchScenario`: fulfilled, but with nothing to render.
                    releasePendingState(state, meta?.requestId);
                }
                if (payload) {
                    state.status = EStatus.Succeeded;
                    state.scenarioId = payload.scenarioId;
                    state.currentPageId = payload.currentPageId;
                    state.mutableWidgetsMap = payload.mutableWidgetsMap;
                    state.bottomSheetMap = payload.bottomSheetMap;
                    state.modalWindowMap = payload.modalWindowMap;
                    state.internalIdWidgets = payload.internalIdWidgets;
                    state.submitWidget = payload.submitWidget;
                    state.rootWidgets = payload.rootWidgets;
                    state.configuration = payload.configuration;
                    state.systemActions = payload.systemActions;
                }
            })
            .addMatcher(isAnyOf(fetchJsonAsync.fulfilled), (state) => {
                state.status = EStatus.Succeeded;
            })
            .addMatcher(isPendingAction, (state, action) => {
                state.status = EStatus.Loading;
                state.pendingRequestId = getRequestId(action);
            })
            .addMatcher(isPendingWidgetAction, (state, action) => {
                // @ts-expect-error
                const aruments = action?.meta?.arg as unknown as TTriggerWidgetAction;
                state.status = aruments?.isShowLoader ? EStatus.Loading : EStatus.WidgetLoading;
                state.pendingRequestId = getRequestId(action);
            })
            .addMatcher(isRejectedAction, (state, { payload, meta }) => {
                if (payload) {
                    state.status = (payload as ValidationErrors)?.isForbidden ? EStatus.Forbidden : EStatus.Failed;
                    state.errors = payload;
                    return;
                }
                // No payload means the request was aborted rather than failed.
                releasePendingState(state, meta?.requestId);
            })
            .addMatcher(isUpdateWidget, (state, { payload, type }) => {
                let widget = null as WritableDraft<TWidget<TMutableWidgetsData, TMutableWidgetsOption>> | null;
                switch (type) {
                    case StoreActionTypeEnum.updateWidgetValueByInternalId: {
                        const action = payload as unknown as IUpdateWidgetValueByInternalId;
                        if (action.internalId) {
                            const widgetUid = state?.internalIdWidgets?.[action.internalId];
                            widget = state?.mutableWidgetsMap?.[widgetUid];
                        }
                        break;
                    }
                    case StoreActionTypeEnum.updateWidgetValue:
                    case StoreActionTypeEnum.updateWidgetValueByKey: {
                        const action = payload as unknown as IUpdateWidgetValue | IUpdateWidgetValueByName;
                        widget = state?.mutableWidgetsMap?.[action.uid];
                        break;
                    }
                    default: {
                        break;
                    }
                }
                if (widget) {
                    widget.systemMetadata['lastValueUpdateAction'] = type;
                }
            });
    },
});

export const {
    updateWidgetValue,
    updateWidgetValueByKey,
    updateWidgetDataByKey,
    updateWidgetPropertyByPath,
    updateModalWindowsMap,
    updateBottomSheetsWidgetsMap,
    updateWidgetValueByInternalId,
    updateInternalIdWidgets,
    setStatus,
    setTheme,
    addMutableWidgets,
    setPlayerId,
    setBottomSheetIsOpen,
} = scenarioSlice.actions;
export const selectRootWidgetUids = (state: IRootState) => state?.scenario?.rootWidgets;
export const selectMutableWidgetsMap = (state: IRootState) => state?.scenario?.mutableWidgetsMap;
export const selectScenarioId = (state: IRootState) => state?.scenario?.scenarioId;
export const selectPlayerId = (state: IRootState) => state?.scenario?.playerId;
export const selectCurrentPageId = (state: IRootState) => state?.scenario?.currentPageId;
export const selectMutableWidget = (state: IRootState, uid: string) => state?.scenario?.mutableWidgetsMap?.[uid];
export const makeSelectMutableWidget = () => createSelector([selectMutableWidgetsMap, (_: IRootState, uid: string) => uid], (map, uid) => map?.[uid]);
export const selectBottomSheets = (state: IRootState) => state?.scenario?.bottomSheetMap;
export const selectModalWindows = (state: IRootState) => state?.scenario?.modalWindowMap;
export const selectSubmitWidget = (state: IRootState) => state?.scenario?.submitWidget;
export const useSystemAction = (systemActionType: ActionSystemActionType): TSystemActin | undefined => {
    return useSelector((state: IRootState) => state?.scenario?.systemActions?.[systemActionType]);
};
export const selectWidgetByInternalId = (state: IRootState, internalId: string) => selectMutableWidget(state, state?.scenario?.internalIdWidgets?.[internalId]);
export const selectError = (state: IRootState) => state?.scenario?.errors;
export const selectStatus = (state: IRootState) => state?.scenario?.status;
export const selectStatusWidgetLoading = (state: IRootState) => state?.scenario?.status === EStatus.WidgetLoading;
export const selectTheme = (state: IRootState) => state?.scenario?.theme;
export const isFailed = (state: IRootState) => state?.scenario?.status === EStatus.Failed;
/**
 * The current failure was caused by a lost connection, not by the backend.
 * `EStatus.Failed` is only ever set together with `errors` (see the `isRejectedAction` matcher),
 * so the flag can never outlive the failure it describes.
 */
export const isFailedByOffline = (state: IRootState) => isFailed(state) && state?.scenario?.errors?.isOffline === true;
export const isShowLoader = (state: IRootState) => state?.scenario?.status === EStatus.Loading || state?.scenario?.status === EStatus.Forbidden;
export const isForbidden = (state: IRootState) => state?.scenario?.status === EStatus.Forbidden;
export const isShowChallenge = (state: IRootState) => state?.scenario?.status === EStatus.ShowChallenge;

export const selectInternalWidgets = (state: IRootState, internalIds: Set<string>): Record<string, TWidget<TMutableWidgetsData>> => {
    const selected: Record<string, TWidget<TMutableWidgetsData>> = {};
    if (internalIds.size === 0) return selected;
    const internalIdWidgets = state?.scenario?.internalIdWidgets;
    const mutableWidgetsMap = state?.scenario?.mutableWidgetsMap;
    if (!internalIdWidgets || !mutableWidgetsMap) return selected;
    for (const internalId of internalIds) {
        const uid = internalIdWidgets[internalId];
        if (uid && mutableWidgetsMap[uid]) {
            selected[internalId] = mutableWidgetsMap[uid] as TWidget<TMutableWidgetsData>;
        }
    }
    return selected;
};

export const useGetInternalWidgets = (internalIds: Set<string>): Record<string, TWidget<TMutableWidgetsData>> => {
    return useSelector((state: IRootState) => selectInternalWidgets(state, internalIds), shallowEqual);
};
